import { createSlice } from "@reduxjs/toolkit";
import {
  getCurrentUser,
  loginUser,
  logoutUser,
  registerUser,
  requestPasswordReset,
  verifyResetCode,
  resetPassword,
  verifyTwoFA,
  resendTwoFactorCode,
} from "../thunks/authThunks";
import { updateUser } from "../../../Profile/redux/thunks/userThunks";

const resolveUserId = (value) => {
  if (!value) return null;
  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized || null;
  }
  if (typeof value !== "object") return null;

  return (
    resolveUserId(value.id) ||
    resolveUserId(value._id) ||
    resolveUserId(value.userId) ||
    resolveUserId(value.sub) ||
    resolveUserId(value.pgId) ||
    resolveUserId(value.uuid) ||
    resolveUserId(value.subprofileId) ||
    resolveUserId(value.subProfileId) ||
    resolveUserId(value.profileId)
  );
};

const sameUser = (current, updated) => {
  const currentId = resolveUserId(current);
  const updatedId = resolveUserId(updated);

  if (currentId && updatedId && String(currentId) === String(updatedId)) {
    return true;
  }

  const currentEmail =
    typeof current?.email === "string" ? current.email.toLowerCase() : null;
  const updatedEmail =
    typeof updated?.email === "string" ? updated.email.toLowerCase() : null;

  return !!currentEmail && currentEmail === updatedEmail;
};

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    resetAuthState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    // ===== getCurrentUser =====
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = "";
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload?.data ?? action.payload;
        state.isAuthenticated = true;
        state.isSuccess = true;
      })
      // ✅ FIX: Ne PAS effacer state.user si getCurrentUser échoue.
      // Avant: state.user = null → déconnectait l'utilisateur à chaque
      // erreur réseau / token doctor / navigation vers /drprofile/:id
      // Maintenant: on garde l'utilisateur connecté, on note juste l'erreur.
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        // ✅ state.user reste inchangé — on ne déconnecte pas l'utilisateur
        state.isError = true;
        state.errorMessage = action.payload || "Unauthorized";
      });

    // ===== loginUser =====
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = "";
        state.isError = false;
        state.isSuccess = false;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload?.user ?? state.user;
        state.isAuthenticated = !!(action.payload?.user ?? state.user);
        state.isSuccess = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = false;
        state.errorMessage = action.payload?.message || action.payload || "Logout failed";
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload || "Login failed";
      });

    // ===== verifyTwoFA =====
    builder
      .addCase(verifyTwoFA.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = "";
        state.isError = false;
      })
      .addCase(verifyTwoFA.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload?.user ?? state.user;
        state.isAuthenticated = true;
        state.isSuccess = true;
      })
      .addCase(verifyTwoFA.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload || "Verification failed";
      });

    // ===== resendTwoFactorCode =====
    builder
      .addCase(resendTwoFactorCode.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = "";
        state.isError = false;
        state.isSuccess = false;
      })
      .addCase(resendTwoFactorCode.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(resendTwoFactorCode.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload || "Failed to resend 2FA code";
      });

    // ===== registerUser =====
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = "";
        state.isError = false;
        state.isSuccess = false;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload?.data || null;
        state.isAuthenticated = !!(action.payload?.data);
        state.isSuccess = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.errorMessage =
          action.payload?.error ||
          action.payload?.message;
      });

    // ===== Password reset flow =====
    builder
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.errorMessage = action.payload;
        state.isError = true;
      })
      .addCase(verifyResetCode.rejected, (state, action) => {
        state.errorMessage = action.payload;
        state.isError = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.errorMessage = action.payload;
        state.isError = true;
      });

    // Keep the authenticated session aligned after profile edits.
    builder.addCase(updateUser.fulfilled, (state, action) => {
      const updatedUser = action.payload;
      if (!state.user || !updatedUser || !sameUser(state.user, updatedUser)) {
        return;
      }

      const id = resolveUserId(updatedUser) || resolveUserId(state.user);

      state.user = {
        ...state.user,
        ...updatedUser,
        ...(id ? { id } : {}),
      };
      state.isAuthenticated = true;
    });
  },
});

export const { logout, setUser, resetAuthState } = authSlice.actions;
export default authSlice.reducer;
